import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import { Form } from "components/ui/form";
import { useForm } from "react-hook-form";
import { Filter, X, FileDown } from "lucide-react";
import { toast } from "components/ui/use-toast";
import { useState } from "react";

import { downloadFromBlob } from "@/utils/download-blob";
import { useExportTransactionReversalMadeToExcelMutation } from "@/services/transactionReversal";

import { useFilterActions } from "../../../ducks/filter/hooks/action";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";

import { FilterStore } from "./FilterSore";
import { FilterOperationType } from "@/pages/financial/transaction-reversal/list/components/filters/FilterOperationType";
import { FilterPaymentType } from "@/pages/financial/transaction-reversal/list/components/filters/FilterPaymentType";
import { FilterRangeDate } from "./FilterRangeDate";

import {
  reversalMadeFiltersSchema,
} from "../../../data/schema";
import {
  MainFiltersFormValues,
  MainFiltersProps,
} from "../../../data/types";

export function MainFilters({
  storesData,
  hasData,
  isLoading,
  onClearData,
}: MainFiltersProps) {
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();
  const [isExporting, setIsExporting] = useState(false);

  const [exportExcel] = useExportTransactionReversalMadeToExcelMutation();

  const form = useForm<MainFiltersFormValues>({
    resolver: zodResolver(reversalMadeFiltersSchema),
    defaultValues: {
      store: filterState?.filters?.advancedFilter?.store || "",
      dateStart: filterState?.filters?.advancedFilter?.dateStart || "",
      dateEnd: filterState?.filters?.advancedFilter?.dateEnd || "",
      operationType:
        filterState?.filters?.advancedFilter?.operationType || "",
      paymentType:
        filterState?.filters?.advancedFilter?.paymentType || "",
    },
  });

  function onSubmit(data: MainFiltersFormValues) {
    updateFilter({
      key: "advancedFilter",
      value: {
        ...filterState?.filters?.advancedFilter,
        ...data,
      },
    });

    toast({
      title: "Filtros aplicados",
      description: "Os filtros foram aplicados com sucesso.",
      duration: 3000,
    });
  }

  function onError(errors: any) {
    const errorMessages = Object.values(errors)
      .map((error: any) => error.message)
      .filter(Boolean);

    if (errorMessages.length > 0) {
      toast({
        variant: "destructive",
        title: "Campos obrigatórios não preenchidos",
        description: errorMessages.join(", "),
        duration: 5000,
      });
    }
  }

  function handleClearFilters() {
    form.reset({
      store: "",
      dateStart: "",
      dateEnd: "",
      operationType: "",
      paymentType: "",
    });

    const advancedFilters = filterState?.filters?.advancedFilter || {};
    const {
      store,
      dateStart,
      dateEnd,
      operationType,
      paymentType,
      ...restFilters
    } = advancedFilters;

    updateFilter({
      key: "advancedFilter",
      value: restFilters,
    });

    onClearData();

    toast({
      title: "Filtros limpos",
      description: "Aplique novos filtros para buscar e exportar dados.",
      duration: 3000,
    });
  }

  async function handleExportExcel() {
    const currentFilters = filterState?.filters?.advancedFilter;

    if (
      !currentFilters?.dateStart ||
      !currentFilters?.dateEnd ||
      !currentFilters?.operationType ||
      !currentFilters?.paymentType
    ) {
      toast({
        variant: "destructive",
        title: "Filtros não aplicados",
        description: "Aplique os filtros obrigatórios antes de exportar.",
        duration: 3000,
      });
      return;
    }

    if (!hasData) {
      toast({
        variant: "destructive",
        title: "Sem dados para exportar",
        description: "Execute uma busca primeiro para exportar dados.",
        duration: 3000,
      });
      return;
    }

    setIsExporting(true);

    try {
      const filters = {
        ...(currentFilters.store !== undefined && {
          storeId: currentFilters.store,
        }),
        ...(currentFilters.dateStart !== undefined && {
          dateStart: currentFilters.dateStart,
        }),
        ...(currentFilters.dateEnd !== undefined && {
          dateEnd: currentFilters.dateEnd,
        }),
        ...(currentFilters.nsuNumber !== undefined && {
          authorizationNsu: currentFilters.nsuNumber,
        }),
        ...(currentFilters.operationType !== undefined && {
          operationType: currentFilters.operationType,
        }),
        ...(currentFilters.paymentType !== undefined && {
          paymentType: currentFilters.paymentType,
        }),
        ...(currentFilters.paymentValue !== undefined && {
          paymentValue: currentFilters.paymentValue,
        }),
        ...(currentFilters.user !== undefined && {
          user: currentFilters.user,
        }),
      };

      const blob = await exportExcel({ filters }).unwrap();

      const now = new Date();
      const dateStr = now.toLocaleDateString("pt-BR").replace(/\//g, "-");
      const timeStr = now.toLocaleTimeString("pt-BR").replace(/:/g, "-");
      const fileName = `Estornos_Realizados_${dateStr}_${timeStr}.xlsx`;

      downloadFromBlob(blob, fileName);

      toast({
        title: "Excel exportado com sucesso!",
        description: "O arquivo foi baixado.",
        duration: 3000,
      });
    } catch (error) {
      console.error("Erro ao exportar Excel:", error);
      toast({
        variant: "destructive",
        title: "Erro ao exportar",
        description: "Ocorreu um erro ao exportar os dados para Excel.",
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  }

  const hasRequiredFilters =
    !!filterState?.filters?.advancedFilter?.dateStart &&
    !!filterState?.filters?.advancedFilter?.dateEnd &&
    !!filterState?.filters?.advancedFilter?.operationType &&
    !!filterState?.filters?.advancedFilter?.paymentType;

  return (
    <div className="bg-white rounded-lg border p-4 mb-4 shadow-sm">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onError)}>
          <div className="flex flex-col gap-4">
            {/* Grid de filtros */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <FilterStore
                control={form.control}
                storesData={storesData}
                errors={form.formState.errors}
              />
              <FilterRangeDate
                control={form.control}
                errors={form.formState.errors}
              />
              <FilterOperationType
                control={form.control}
                errors={form.formState.errors}
              />
              <FilterPaymentType
                control={form.control}
                errors={form.formState.errors}
              />
            </div>

            {/* Botões de ação */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
              >
                <X className="h-4 w-4 mr-2" />
                Limpar Filtros
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={
                  !hasData || !hasRequiredFilters || isLoading || isExporting
                }
                title={
                  !hasRequiredFilters
                    ? "Aplique os filtros obrigatórios antes de exportar"
                    : !hasData
                    ? "Execute uma busca primeiro para exportar dados"
                    : "Exportar dados filtrados para Excel"
                }
              >
                <FileDown className="h-4 w-4 mr-2" />
                {isExporting ? "Exportando..." : "Exportar Excel"}
              </Button>
              <Button type="submit" variant="default" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
